import { ChangeDetectionStrategy, Component, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

import { PeopleListBloc } from '@connect-services/client/blocs/people-list.bloc';
import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { ComplexModalTemplate } from '@connect-services/client/components/complexe-modal/complex-modal-template.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import {
    PeopleService,
    Person,
    PersonControllerGetRequestParams,
    ProjectsService,
} from '@connect-services/client/sdk/api';
import { SecurityService } from '@connect-services/client/services/security.service';
import { SelectComponent, SelectOption } from '@connect-services/src/lib/components/select/select.component';
import { defaultPaginationParameters, OrderEnum, paginationMaxTake } from '@shared/client/models/pagination.model';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ComplexModalTemplate, SelectComponent, TranslocoPipe],
    selector: 'app-edit-user-access-modal',
    templateUrl: './edit-user-access-modal.component.html',
})
export class EditUserAccessModalComponent implements OnInit {
    protected dialogRef = inject(MatDialogRef<EditUserAccessModalComponent>);
    protected readonly peopleListBloc = inject(PeopleListBloc);
    protected readonly peopleOptions: WritableSignal<SelectOption<Person>[]> = signal([]);
    protected readonly selectedPeopleOptions: WritableSignal<SelectOption<Person>[]> = signal([]);

    private readonly peopleService = inject(PeopleService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly projectsService = inject(ProjectsService);
    private readonly securityService = inject(SecurityService);
    private readonly snackBarService = inject(SnackBarService);
    private readonly translocoService = inject(TranslocoService);

    public async ngOnInit(): Promise<void> {
        if (!this.projectBloc.value()?.id) {
            return;
        }

        await this.setOptions();
    }

    public async onClickEvent(modalResult: ModalResultEnum): Promise<void> {
        if (modalResult === ModalResultEnum.CANCEL) {
            this.dialogRef.close(null);

            return;
        }
        const selectedPeopleIds = this.selectedPeopleOptions().map((person: SelectOption<Person>) => person.value.id);

        try {
            await firstValueFrom(
                this.projectsService.projectControllerPatch({
                    id: this.projectBloc.value()!.id,
                    updateProjectDto: {
                        people: selectedPeopleIds,
                    },
                }),
            );

            await this.peopleListBloc.load(
                {
                    ...defaultPaginationParameters,
                    order: OrderEnum.ASC,
                    orderBy: 'familyName',
                    project: this.projectBloc.value()!.id,
                },
                { refresh: true },
            );

            this.snackBarService.openSuccessSnackBar('modificationsSaveSuccess');
            this.dialogRef.close();
        } catch {
            this.snackBarService.openErrorSnackBar('errorPATCH');
        }
    }

    public async setAvailablePeople(): Promise<SelectOption<Person>[]> {
        const availablePeople = await this.fetchPeople({
            notInProject: this.projectBloc.value()!.id,
            take: 10,
        });

        return availablePeople.map((person: Person): SelectOption<Person> => ({
            label: person.fullName,
            value: person,
        }));
    }

    public async setSelectedPeople(): Promise<SelectOption<Person>[]> {
        const peopleInProject = await this.fetchPeople({
            project: this.projectBloc.value()!.id,
            take: paginationMaxTake,
        });

        const mappedPeople = await this.mapSelectedPeople(peopleInProject);

        this.selectedPeopleOptions.set(mappedPeople);

        return mappedPeople;
    }

    private async fetchPeople(params: PersonControllerGetRequestParams): Promise<Person[]> {
        const people: Person[] = [];
        let hasNextPage = false;
        let page = 0;

        do {
            const peoplePagination = await firstValueFrom(
                this.peopleService.personControllerGet({ ...params, page: page }),
            );

            people.push(...peoplePagination.items);
            page += 1;
            hasNextPage = peoplePagination.hasNextPage;
        } while (hasNextPage);

        return people;
    }

    private async getOperatorIds(): Promise<string[]> {
        const operators: Person[] = [];
        let hasNextPage = false;
        let page = 0;

        do {
            const peopleInProjectPagination = await firstValueFrom(
                this.peopleService.personControllerGetFieldInterventionOperators({
                    page: page,
                    project: this.projectBloc.value()!.id,
                    take: paginationMaxTake,
                }),
            );

            operators.push(...peopleInProjectPagination.items);
            page += 1;
            hasNextPage = peopleInProjectPagination.hasNextPage;
        } while (hasNextPage);

        return operators.map((operator: Person) => operator.id);
    }

    private async mapSelectedPeople(peopleInProject: Person[]): Promise<SelectOption<Person>[]> {
        const operatorIds = await this.getOperatorIds();

        return peopleInProject.map((person: Person): SelectOption<Person> => {
            let disabledReason = undefined;

            if (operatorIds.includes(person.id)) {
                disabledReason = this.translocoService.translate('warningUserInFieldIntervention');
            }

            if (this.securityService.meSignal()?.id === person.id) {
                disabledReason = this.translocoService.translate('warningRemovingOwnAccess');
            }

            return {
                defaultSelected: !!disabledReason,
                disabledReason: disabledReason,
                label: person.fullName,
                value: person,
            };
        });
    }

    private async setOptions(): Promise<void> {
        const selectedPeople = await this.setSelectedPeople();
        const availablePeople = await this.setAvailablePeople();

        const uniquePeopleMap = new Map<string, SelectOption<Person>>(
            [...selectedPeople, ...availablePeople]
                .sort((a: SelectOption<Person>, b: SelectOption<Person>) =>
                    a.value.familyName.localeCompare(b.value.familyName),
                )
                .map((person: SelectOption<Person>) => [person.value.id, person]),
        );

        this.peopleOptions.set(Array.from(uniquePeopleMap.values()));
    }
}
